import { FC, memo, useEffect, useState } from 'react';
import { Stack, VStack } from '@chakra-ui/react';
import {
  useAppContext,
  useProgram,
  usePromotions,
} from '@frx/frx-react-context';
import isEmpty from 'lodash/fp/isEmpty';
import isEqual from 'lodash/fp/isEqual';

import {
  Achievements,
  Announcements,
  CustomizeUrl,
  Donations,
} from './columns';
import {
  BoundlessAppLink,
  EventDetails,
  Leaderboards,
  Notification,
  Wrapper,
} from '../../components';
import { YourTeam } from '../team/columns';
import {
  useEventId,
  useIndividualLeaders,
  useTeamLeaders,
  useTeams,
} from '../../hooks';

import {
  FragmentIdentifier,
  showBoundlessApps,
  shuffleArray,
  TeamMemberProps,
  getContentFragment,
  getFullImagePath,
  isDiyEvent,
  isMemorial,
} from '../../utils';

export const Dashboard: FC = memo(() => {
  const eventId = useEventId();
  const {
    data: {
      eventData: { eventInfo, eventConfig },
      authData: { user },
    },
  } = useAppContext();

  const [shuffledTeam, setShuffledTeam] = useState<TeamMemberProps[]>([]);
  const {
    teamsAllowed,
    isLoadingTeams,
    teamInfo,
    teamPhoto,
    teamMembers,
    captainsMessage,
  } = useTeams(eventId);

  const { data: promotions, isLoading: isLoadingPromotions } = usePromotions({
    variation: eventInfo?.eventType?.toLowerCase(),
  });

  const { data: eventDetailsImage } = useProgram({ variation: `${eventId}` });
  const { individualLeaders, isLoading: isLoadingIndividualLeaders } =
    useIndividualLeaders();

  const { teamLeaders, isLoading: isLoadingTeamLeaders } = useTeamLeaders();

  const isLoading =
    isLoadingTeams ||
    isLoadingPromotions ||
    isLoadingIndividualLeaders ||
    isLoadingTeamLeaders;

  // Only shuffle the team after teams are loaded. This prevents excessive renders
  // on child component <YourTeam>
  useEffect(() => {
    if (teamsAllowed && !isLoading) {
      setShuffledTeam(shuffleArray(Array.from(teamMembers || [])));
    }
    // Adding teamMembers as an additional array dependency cause unnecessary renders,
    // so disable warning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // AEM CMS provides an array of promotional content items. We need to
  // identify the boundless app in this array. It is tagged as boundless by leveraging the unused cssClass
  // and matching against 'boundless'.
  const boundlessApp = promotions?.data.promotionalList.items.find((promo) =>
    promo?.ctaCssClass[0].includes('boundless')
  );

  const heading = boundlessApp?.heading || '';
  const appleStoreLink = boundlessApp?.ctaLink[0] || '';
  const googlePlayLink = boundlessApp?.ctaLink[1] || '';
  const imgSrc = getContentFragment(
    eventDetailsImage?.data?.programList.items || [],
    FragmentIdentifier.eventDetails,
    eventInfo?.eventType?.toLowerCase()
  );
  const imgPath = getFullImagePath(imgSrc?.image?._path);

  return (
    <VStack spacing={6}>
      {teamsAllowed && !isEmpty(captainsMessage) && (
        <Notification
          status="info"
          message={captainsMessage}
          trackingLabel="teamAnnouncement"
          hasClose
        />
      )}

      <Stack
        direction={['column', 'column', 'column', 'row']}
        spacing={[0, 0, 6]}
      >
        <Wrapper>
          <Donations />
          <CustomizeUrl />
          {teamsAllowed && user?.isOnTeam && (
            <YourTeam
              teamInfo={teamInfo}
              teamMembers={shuffledTeam}
              teamPhoto={teamPhoto}
              showCompanyName
              showLeaveTeam={false}
              showTeamProgress={false}
            />
          )}

          {!isDiyEvent(eventInfo?.eventType) &&
            !isMemorial(String(eventId)) && (
              <EventDetails
                image={imgPath}
                eventInfo={eventInfo}
                isEditable={eventConfig?.personalSurveyEditAllowed}
              />
            )}
        </Wrapper>
        <Wrapper>
          <Announcements />
          {!isMemorial(String(eventId)) && (
            <>
              <Achievements />
              <Leaderboards
                teamsAllowed={teamsAllowed}
                individualLeaders={individualLeaders}
                teamLeaders={teamLeaders}
              />
            </>
          )}

          {showBoundlessApps(eventInfo?.eventType) && (
            <BoundlessAppLink
              heading={heading}
              appleStoreLink={appleStoreLink}
              googlePlayLink={googlePlayLink}
            />
          )}
        </Wrapper>
      </Stack>
    </VStack>
  );
}, isEqual);
